import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IoSearch, IoTrashOutline } from "react-icons/io5";
import { IoMdArchive } from "react-icons/io";
import { MdOutlineWifiTetheringErrorRounded } from "react-icons/md";
import { HiOutlinePencilAlt } from "react-icons/hi";
import { IoFilter } from "react-icons/io5";
import Search from "../../Jobick/Jobs/ui/search";
import { toast } from "react-toastify";
import CategoryService from "../../../../services/CategoryService";
import { colors } from "../../../config/colors";
import { getJobTitleLabel } from "../../../utils/jobTitleLabel";

import DeleteModal from "../../shared/DeleteModal";

const ListCategory = () => {
  const jobLabel = getJobTitleLabel();
  const jobPluralLabel = `${jobLabel}${jobLabel.toLowerCase().endsWith("s") ? "" : "s"}`;
  const navigate = useNavigate();
  const [pageVal, setPageVal] = useState(1);
  const [totalCategories, setTotalCategories] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(1);
  const [categoryList, setCategoryList] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showArchivedCategories, setShowArchivedCategories] = useState(false);
  const [archivingCategory, setArchivingCategory] = useState(null);
  const categoriesPerPage = 10; // Fixed limit of 10 items per page
  const [activeCategories, setActiveCategories] = useState(0);
  const [inactiveCategories, setInactiveCategories] = useState(0);
  const SEARCH_DEBOUNCE_DELAY = 500;
  
  // Add filter state
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "inactive"
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const handlePreviousPageClick = () => {
    if (pageVal === 1) return;
    setPageVal(pageVal - 1);
  };

  const handleNextPageClick = () => {
    if (pageVal >= totalPages) return;
    setPageVal(pageVal + 1);
  };

  const handleDeleteClick = (categoryId) => {
    setCategoryToDelete(categoryId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await CategoryService.deleteCategory(categoryToDelete);
      if (response.success) {
        toast.success("Category deleted successfully");
        fetchCategoryList();
      } else {
        throw new Error(response.message || "Failed to delete category");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete category"
      );
    } finally {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  const handleArchiveCategory = async (categoryId, isArchived = true) => {
    try {
      setArchivingCategory(categoryId);
      const response = await CategoryService.archiveCategory(categoryId, isArchived);
      if (response.success) {
        toast.success(isArchived ? "Category archived successfully" : "Category unarchived successfully");
        fetchCategoryList();
      } else {
        throw new Error(response.error || `Failed to ${isArchived ? 'archive' : 'unarchive'} category`);
      }
    } catch (error) {
      console.error(`Error ${isArchived ? 'archiving' : 'unarchiving'} category:`, error);
      const errorMessage = error.response?.data?.error || error.message || `Failed to ${isArchived ? 'archive' : 'unarchive'} category`;
      toast.error(errorMessage);
    } finally {
      setArchivingCategory(null);
    }
  };

  const fetchCategoryList = async () => {
    setLoading(1);
    try {
      // Convert statusFilter to isActive parameter
      let isActive = null;
      if (statusFilter === "active") {
        isActive = true;
      } else if (statusFilter === "inactive") {
        isActive = false;
      }

      const response = await CategoryService.getCategories(
        pageVal,
        categoriesPerPage,
        searchVal,
        showArchivedCategories,
        isActive
      );

      if (response.success) {
        setCategoryList(response.data || []);
        setTotalCategories(response.count || 0);
        setTotalPages(response.totalPages || 1);
        setLoading(0);
        setActiveCategories(response?.activeCategories || 0);
        setInactiveCategories(response?.inactiveCategories || 0);
      } else {
        throw new Error(response.message || "Failed to fetch categories");
      }
    } catch (error) {
      setLoading(2);
      console.error("Error fetching category list:", error);
      toast.error(error.message || "Failed to fetch categories");
    }
  };

  useEffect(() => {
    fetchCategoryList();
  }, [pageVal, showArchivedCategories, statusFilter]);

  useEffect(() => {
    if (pageVal === 1) {
      fetchCategoryList();
    } else {
      setPageVal(1);
    }
  }, [searchVal, showArchivedCategories, statusFilter]);

  const handleCreateCategory = () => {
    navigate("/create-category");
  };

  const handleEditClick = (categoryId) => {
    navigate(`/edit-category/${categoryId}`);
  };

  const handleToggleActive = async (category) => {
    try {
      const updatedActive = !category.isActive;
      const response = await CategoryService.updateCategory(category._id, {
        ...category,
        isActive: updatedActive
      });
      
      if (response.data.success) {
        toast.success("Category status updated successfully");
        let newCategoryList = categoryList.map(cat => cat._id === category._id ? response.data.data : cat);
        setCategoryList(newCategoryList);
        if (updatedActive) {
          setActiveCategories(activeCategories + 1);
          setInactiveCategories(inactiveCategories - 1);
        } else {
          setActiveCategories(activeCategories - 1);
          setInactiveCategories(inactiveCategories + 1);
        }
      } else {
        throw new Error(response.message || "Failed to update category status");
      }
    } catch (error) {
      console.error("Error updating category status:", error);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to update category status"
      );
    }
  };

  const pgItem1 =
    pageVal === 1
      ? 1
      : pageVal >= totalPages && totalPages > 2
        ? totalPages - 2
        : pageVal - 1;
  const pgItem2 =
    pageVal === 1
      ? 2
      : pageVal >= totalPages && totalPages > 2
        ? totalPages - 1
        : pageVal;
  const pgItem3 =
    pageVal === 1
      ? 3
      : pageVal >= totalPages && totalPages > 2
        ? totalPages
        : pageVal + 1;

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchVal(value);
    }, SEARCH_DEBOUNCE_DELAY),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  function debounce(func, wait) {
    let timeout;
    const debouncedFunc = function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
    debouncedFunc.cancel = function () {
      clearTimeout(timeout);
    };
    return debouncedFunc;
  }

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.filter-dropdown')) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 p-6">
      {/* Header Section with Stats */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-2xl font-bold text-primary-headings mb-2">
              {showArchivedCategories ? `Archived ${jobLabel} Categories` : `${jobLabel} Categories Management`}
            </h2>
            <p className="text-sm text-primary-light mb-6">
              {showArchivedCategories 
                ? `View and manage archived ${jobLabel.toLowerCase()} categories` 
                : `Track and manage your ${jobLabel.toLowerCase()} categories efficiently${
                    statusFilter !== "all" 
                      ? ` - Showing ${statusFilter === "active" ? "active" : "inactive"} categories only`
                      : ""
                  }`
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowArchivedCategories(!showArchivedCategories)}
              className={`inline-flex items-center px-4 py-2 rounded-md border transition-colors duration-200 shadow-sm whitespace-nowrap ${
                showArchivedCategories 
                  ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <IoMdArchive className="h-4 w-4 mr-2" />
              <span className="text-sm">{showArchivedCategories ? 'Show Active Categories' : 'Show Archived Categories'}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="bg-blue-50 p-3 rounded-lg">
                <svg className="w-6 h-6 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-primary-light">Total Categories</p>
                <p className="text-xl font-semibold text-primary-headings">{totalCategories}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="bg-green-50 p-3 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-primary-light">Active Categories</p>
                <p className="text-xl font-semibold text-primary-headings">
                  {activeCategories}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="bg-purple-50 p-3 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-primary-light">Usage Rate</p>
                <p className="text-xl font-semibold text-primary-headings">
                  {totalCategories > 0 ? (Math.round((activeCategories / totalCategories) * 100)) : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Actions Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent text-sm"
              placeholder={showArchivedCategories ? "Search archived categories..." : "Search categories..."}
            />
          </div>
          
          {/* Status Filter Dropdown */}
          {!showArchivedCategories && (
            <div className="relative filter-dropdown">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="inline-flex items-center px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-transparent transition-colors duration-200"
              >
                <IoFilter className="h-4 w-4 mr-2" />
                {statusFilter === "all" && "All Status"}
                {statusFilter === "active" && "Active Only"}
                {statusFilter === "inactive" && "Inactive Only"}
                <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {statusFilter !== "all" && (
                <button
                  onClick={() => setStatusFilter("all")}
                  className="ml-2 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors duration-200"
                  title="Clear filter"
                >
                  Clear
                </button>
              )}
              
              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setStatusFilter("all");
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        statusFilter === "all" ? "text-primary-headings bg-primary-light/10" : "text-gray-700"
                      }`}
                    >
                      All Status
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter("active");
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        statusFilter === "active" ? "text-primary-headings bg-primary-light/10" : "text-gray-700"
                      }`}
                    >
                      Active Only
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter("inactive");
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        statusFilter === "inactive" ? "text-primary-headings bg-primary-light/10" : "text-gray-700"
                      }`}
                    >
                      Inactive Only
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {!showArchivedCategories && (
            <button 
              onClick={handleCreateCategory}
              className="inline-flex items-center px-6 py-2.5 text-white rounded-lg hover:bg-opacity-90 transition-all duration-200 shadow-sm font-medium whitespace-nowrap"
              style={{ backgroundColor: colors.highlights.callToAction }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Category
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden max-h-[calc(100vh-480px)] flex flex-col mb-8">
        <div className="overflow-x-auto flex-1">
          <table className="w-full">
            <thead className="bg-white z-10 after:absolute after:inset-x-0 after:bottom-0 after:h-[1px] after:bg-gray-200">
              <tr>
                <th className="w-[60px] min-w-[60px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">No</th>
                <th className="w-full min-w-[200px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Category Type</th>
                <th className="w-[100px] min-w-[100px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Status</th>
                <th className="w-[100px] min-w-[100px] px-4 py-3 text-center text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading === 1 && (
                <tr>
                  <td colSpan="4" className="px-4 py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-8 h-8 border-3 border-t-3 border-primary-light border-t-primary-headings rounded-full animate-spin"></div>
                    </div>
                  </td>
                </tr>
              )}

              {loading === 0 && categoryList.map((category, index) => (
                <tr key={category._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="w-[60px] min-w-[60px] px-4 py-3 text-sm font-medium text-primary-headings whitespace-nowrap">
                    {(pageVal - 1) * categoriesPerPage + index + 1}
                  </td>
                  <td className="w-full min-w-[200px] px-4 py-3">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-primary-headings truncate">
                        {category.categoryType}
                        {showArchivedCategories && (
                          <span className="text-xs text-yellow-600 ml-2 bg-yellow-50 px-2 py-1 rounded-full italic">Archived</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="w-[100px] min-w-[100px] px-4 py-3">
                    <span 
                      onClick={() => !showArchivedCategories && handleToggleActive(category)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        showArchivedCategories 
                          ? 'bg-yellow-100 text-yellow-800 cursor-default'
                          : category.isActive 
                            ? 'bg-green-100 text-green-800 cursor-pointer hover:opacity-80' 
                            : 'bg-red-100 text-red-800 cursor-pointer hover:opacity-80'
                      }`}
                    >
                      {showArchivedCategories ? "Archived" : (category.isActive ? "Active" : "Inactive")}
                    </span>
                  </td>
                  <td className="w-[100px] min-w-[100px] px-4 py-3">
                    <div className="flex justify-center space-x-2 overflow-x-auto">
                      {!showArchivedCategories && (
                        <button
                          onClick={() => handleEditClick(category._id)}
                          className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors duration-200 shrink-0"
                        >
                          <HiOutlinePencilAlt size={18} className="text-indigo-500" />
                        </button>
                      )}
                      <button
                        onClick={() => handleArchiveCategory(category._id, !showArchivedCategories)}
                        className="p-1.5 hover:bg-yellow-50 rounded-lg transition-colors duration-200 shrink-0"
                        title={showArchivedCategories ? "Unarchive" : "Archive"}
                        disabled={archivingCategory === category._id}
                      >
                        {archivingCategory === category._id ? (
                          <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <IoMdArchive size={18} className="text-yellow-600" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading === 2 && (
          <div className="flex flex-col items-center py-8">
            <MdOutlineWifiTetheringErrorRounded size={120} className="text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-800 mt-4">Error fetching data</h2>
            <h3 className="text-gray-500 mt-2">Check your network and try again</h3>
          </div>
        )}

        {loading === 0 && categoryList.length === 0 && (
          <div className="flex flex-col items-center py-8">
            <IoSearch size={120} className="text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-800 mt-4">No categories found</h2>
            <h3 className="text-gray-500 mt-2">Try adjusting your search to find what you're looking for</h3>
          </div>
        )}

        {/* Pagination Section */}
        {loading === 0 && categoryList.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 bg-white">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-primary-light">
                Showing <span className="font-medium">{categoriesPerPage * (pageVal - 1) + 1}</span> to{' '}
                <span className="font-medium">{categoriesPerPage * (pageVal - 1) + categoryList.length}</span> of{' '}
                <span className="font-medium">{totalCategories}</span> results
              </p>
              
              <nav className="flex items-center gap-2">
                <button
                  onClick={handlePreviousPageClick}
                  disabled={pageVal === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50
                    ${pageVal === 1 ? 'bg-gray-100 text-primary-light' : 'bg-primary-headings text-white'}`}
                >
                  Previous
                </button>
                
                {totalPages > 0 && (
                  <>
                    <button
                      className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                        ${pageVal === pgItem1 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                      onClick={() => setPageVal(pgItem1)}
                    >
                      {pgItem1}
                    </button>
                    {totalPages > 1 && (
                      <button
                        className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                          ${pageVal === pgItem2 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                        onClick={() => setPageVal(pgItem2)}
                      >
                        {pgItem2}
                      </button>
                    )}
                    {totalPages > 2 && (
                      <button
                        className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                          ${pageVal === pgItem3 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                        onClick={() => setPageVal(pgItem3)}
                      >
                        {pgItem3}
                      </button>
                    )}
                  </>
                )}
                
                <button
                  onClick={handleNextPageClick}
                  disabled={pageVal >= totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50
                    ${pageVal >= totalPages ? 'bg-gray-100 text-primary-light' : 'bg-primary-headings text-white'}`}
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <DeleteModal
          deleteItem="Category"
          onDelete={handleConfirmDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setCategoryToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default ListCategory;
